import { widgetsOfKind, type WidgetDef, type WidgetKind } from './widgets';

/**
 * Turning what a person saved in Settings › Dashboard into what they actually
 * see. Pure, so the rules that matter are tested without rendering anything.
 *
 * Three rules, in this order:
 * 1. **Permission wins.** A widget nobody may see is never shown, whatever was
 *    saved. Losing access hides it; getting it back brings it where it was.
 * 2. **New widgets appear.** A widget added after someone saved their layout is
 *    not in their list. It is shown, in its designed place — just after the
 *    widget it follows by default — so new things are not silently hidden.
 * 3. **Stale ids are ignored.** An id for a widget that no longer exists is
 *    dropped rather than breaking the order around it.
 */

export interface ListLayout {
  order?: string[];
  hidden?: string[];
}

export interface DashboardLayout {
  cards?: ListLayout;
  sections?: ListLayout;
}

export interface Arranged {
  /** Shown, in order. */
  shown: WidgetDef[];
  /** Available but switched off, in order — what Settings lists unticked. */
  hidden: WidgetDef[];
  /** Everything the person may see, in order, shown or not. */
  available: WidgetDef[];
}

/**
 * Orders the ids a person may see: their saved order first, with anything new
 * slotted into the place it holds by default.
 */
export function mergeOrder(
  defaultOrder: readonly string[],
  savedOrder: readonly string[] | undefined,
): string[] {
  const known = new Set(defaultOrder);
  // Stale ids go, and a duplicate cannot put one widget in two places.
  const saved = (savedOrder ?? []).filter(
    (id, i, all) => known.has(id) && all.indexOf(id) === i,
  );
  if (saved.length === 0) return [...defaultOrder];

  const result = [...saved];
  for (const [index, id] of defaultOrder.entries()) {
    if (result.includes(id)) continue;

    // Put it back beside the widget it follows by default.
    let at = 0;
    for (let before = index - 1; before >= 0; before--) {
      const previous = defaultOrder[before]!;
      const found = result.indexOf(previous);
      if (found !== -1) {
        at = found + 1;
        break;
      }
    }
    result.splice(at, 0, id);
  }
  return result;
}

/** What one list of the dashboard shows for this person. */
export function arrangeWidgets(
  kind: WidgetKind,
  layout: DashboardLayout | null | undefined,
  can: (permission: string) => boolean,
  /** Sections the server sent. Undefined skips the check, for Settings. */
  present?: (widget: WidgetDef) => boolean,
): Arranged {
  const defs = widgetsOfKind(kind);
  const allowed = defs.filter(
    (w) => (w.permission === '*' || can(w.permission)) && (present?.(w) ?? true),
  );
  const byId = new Map(allowed.map((w) => [w.id, w]));

  const saved = kind === 'card' ? layout?.cards : layout?.sections;
  const order = mergeOrder(
    allowed.map((w) => w.id),
    saved?.order,
  );
  const hidden = new Set(saved?.hidden ?? []);

  const available = order
    .map((id) => byId.get(id))
    .filter((w): w is WidgetDef => w !== undefined);

  return {
    available,
    shown: available.filter((w) => !hidden.has(w.id)),
    hidden: available.filter((w) => hidden.has(w.id)),
  };
}

/** The layout with one widget switched on or off. */
export function withVisibility(
  layout: DashboardLayout | null | undefined,
  kind: WidgetKind,
  id: string,
  show: boolean,
  /** The order to write down, so hiding does not also lose the arrangement. */
  order: readonly string[],
): DashboardLayout {
  const key = kind === 'card' ? 'cards' : 'sections';
  const current = layout?.[key];
  const hidden = new Set(current?.hidden ?? []);
  if (show) hidden.delete(id);
  else hidden.add(id);

  return {
    ...layout,
    [key]: { order: [...order], hidden: [...hidden] },
  };
}

/** The layout with one widget moved one place up or down. */
export function withMove(
  layout: DashboardLayout | null | undefined,
  kind: WidgetKind,
  id: string,
  direction: 'up' | 'down',
  /** The order as the person currently sees it. */
  order: readonly string[],
): DashboardLayout {
  const key = kind === 'card' ? 'cards' : 'sections';
  const next = [...order];
  const from = next.indexOf(id);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from === -1 || to < 0 || to >= next.length) return layout ?? {};

  next.splice(to, 0, ...next.splice(from, 1));
  return {
    ...layout,
    [key]: { order: next, hidden: [...(layout?.[key]?.hidden ?? [])] },
  };
}
